import React from 'react';
import { string, object, arrayOf } from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage } from '../../../util/reactIntl';

import { NamedLink } from '../../../components';
import { getStoreImageUrl } from '../../../util/sellerImages';

import css from './SectionFeaturedDealers.module.css';

const StarIcon = ({ filled }) => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M7.08611 2.05727C7.43796 1.26523 8.56204 1.26523 8.91389 2.05728L9.97053 4.43591C10.1158 4.76296 10.4248 4.98744 10.7807 5.02455L13.3695 5.29444C14.2315 5.38431 14.5788 6.45338 13.9343 7.03276L11.9986 8.77272C11.7324 9.01196 11.6144 9.37518 11.6891 9.72516L12.2324 12.2706C12.4133 13.1182 11.5039 13.7789 10.7537 13.3449L8.50073 12.0417C8.19096 11.8625 7.80904 11.8625 7.49927 12.0417L5.24631 13.3449C4.49611 13.7789 3.5867 13.1182 3.7676 12.2706L4.31089 9.72516C4.38559 9.37518 4.26757 9.01196 4.00142 8.77272L2.06573 7.03275C1.42117 6.45338 1.76854 5.38431 2.63054 5.29444L5.21928 5.02455C5.57521 4.98744 5.88419 4.76296 6.02947 4.43591L7.08611 2.05727Z" fill={filled ? '#6E712E' : '#D9D9D9'} />
  </svg>
);

const StarRating = ({ rating }) => (
  <span className={css.stars}>
    {[1, 2, 3, 4, 5].map(star => (
      <StarIcon key={star} filled={star <= rating} />
    ))}
  </span>
);

const DealerCard = ({ dealer }) => {
  const profile = dealer?.attributes?.profile || {};
  const { publicData } = profile;
  const { businessName, storedAddress } = publicData || {};
  const name = businessName || profile.displayName || profile.abbreviatedName || '';

  const reviews = dealer?.attributes?.reviews || [];
  const reviewQty = reviews.length;
  const rating = reviewQty > 0
    ? Math.round(reviews.reduce((sum, r) => sum + (r.rating || 0), 0) / reviewQty)
    : 0;

  // Get store image
  const storeImage = getStoreImageUrl(dealer) || '/static/footer-logo.png';

  // Get location - extract city, state from place_name
  // place_name can be a street address ("123 Main St, City, State ZIP, Country")
  // or a place ("City, State, Country") — detect by checking if first part starts with a digit
  const fullLocation = storedAddress?.place_name || '';
  const locationParts = fullLocation.split(',').map(s => s.trim());
  const isStreetAddress = /^\d/.test(locationParts[0]);
  const city = isStreetAddress ? locationParts[1] : locationParts[0];
  const rawState = isStreetAddress ? locationParts[2] : locationParts[1];
  // Strip zip code from state (e.g. "California 94063" → "California")
  const state = (rawState || '').replace(/\s+\d+.*$/, '').trim();
  const location = city && state ? `${city}, ${state}` : city || state || '';

  return (
    <NamedLink
      name="TeamPage"
      params={{ id: dealer.id.uuid, type: 'detail' }}
      className={css.dealerCard}
    >
      <div className={css.storeImageContainer}>
        <img src={storeImage} className={css.storeImage} alt={name} />
      </div>
      <div className={css.storeInfo}>
        <div className={css.nameGroup}>
          <p className={css.storeName}>{name}</p>
          <svg width="139" height="2" viewBox="0 0 139 2" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M1 1H138" stroke="#D9D9D9" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </div>
        <div className={css.locationReviews}>
          {location && <p className={css.storeLocation}>{location}</p>}
          {reviewQty > 0 && (
            <div className={css.ratingContainer}>
              <StarRating rating={rating} />
              <span className={css.reviewQty}>({reviewQty} reviews)</span>
            </div>
          )}
        </div>
      </div>
    </NamedLink>
  );
};

const SectionFeaturedDealers = props => {
  const { className, rootClassName, stores } = props;

  const classes = classNames(rootClassName || css.root, className);

  // Handle both data structures: stores.data (object) or stores (array)
  let storesArray = [];
  if (stores?.data) {
    // If stores is an object with data property
    storesArray = Object.values(stores.data);
  } else if (Array.isArray(stores)) {
    // If stores is directly an array
    storesArray = stores;
  }

  // Sort dealers by listings count, limit to 2 rows (8 cards)
  const dealers = storesArray
    .sort((a, b) => (b.listings?.length || 0) - (a.listings?.length || 0))
    .slice(0, 8);

  const hasDealers = dealers.length > 0;

  return (
    <section className={classes}>
      <div className={css.container}>
        <div className={css.header}>
          <h2 className={css.title}>
            <FormattedMessage id="SectionFeaturedDealers.title" />
          </h2>
          <NamedLink
            name="DealersDirectoryPage"
            className={css.seeAllButton}
          >
            <FormattedMessage id="SectionFeaturedDealers.seeAllDealers" />
          </NamedLink>
        </div>

        {hasDealers ? (
          <div className={css.dealersGrid}>
            {dealers.map(dealer => (
              <DealerCard
                key={dealer.id.uuid}
                dealer={dealer}
              />
            ))}
          </div>
        ) : (
          <div className={css.noDealers}>
            <p>
              <FormattedMessage id="SectionFeaturedDealers.noDealers" />
            </p>
          </div>
        )}
      </div>
    </section>
  );
};

SectionFeaturedDealers.defaultProps = {
  className: null,
  rootClassName: null,
  stores: null,
};

SectionFeaturedDealers.propTypes = {
  className: string,
  rootClassName: string,
  stores: arrayOf(object),
};

export default SectionFeaturedDealers;
